// Phone People list (view 'people') — window.PhonePeople.
//
// The desktop PeopleView (buyers_gpp.jsx) is a 318k-row column grid with
// inline editing, import/export and a column menu. On a phone the job is
// narrower: find a person and call or email them, or open their page. So
// this is a search-first list over the same /api/contacts query (my_team
// scope, server-side q + filters, page by page), with Call / Email buttons
// on every row. It never touches the desktop grid's column prefs; the list
// as it was left (for Back) is kept in memory only.
//
// Also home to window.AtlasPhone — parsing DealCloud's free-text phone
// field into tel:/sms: links — shared with the phone contact page.

// ── Phone numbers ───────────────────────────────────────────────────────────
// DealCloud phones are free text: "(916) 468-8893    (Work)", "555-1234 x12",
// two numbers in one field ("… ; …"), or junk like ".    (Work)". Split on
// the separators people actually use, pull off a trailing "(Work)"-style
// label and an extension, and drop anything with too few digits to dial —
// a dead tel: link is worse than none.
const parsePhones = (raw) => {
  if (!raw) return [];
  const out = [];
  const seen = new Set();
  String(raw).split(/\s*(?:[;|\n]|\s\/\s|\bor\b)\s*/i).forEach((part) => {
    let s = (part || '').replace(/\s+/g, ' ').trim();
    if (!s) return;
    let label = null;
    // A label starts with a letter, so an area code "(916)" never matches.
    const lm = /\(([A-Za-z][A-Za-z .-]*)\)/.exec(s);
    if (lm) {
      label = lm[1].trim().toLowerCase();
      s = s.replace(lm[0], ' ').replace(/\s+/g, ' ').trim();
    }
    let ext = null;
    const em = /(?:ext(?:ension)?\.?|x)\s*(\d{1,6})\s*$/i.exec(s);
    if (em) {
      ext = em[1];
      s = s.slice(0, em.index).replace(/[\s,.-]+$/, '').trim();
    }
    const digits = s.replace(/[^\d+]/g, '');
    if ((digits.match(/\d/g) || []).length < 7) return;
    const key = digits + '|' + (ext || '');
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      display: ext ? `${s} ext. ${ext}` : s,
      label,
      digits,
      // A comma is the dialler's pause — the extension is keyed after pickup.
      tel: `tel:${digits}${ext ? ',' + ext : ''}`,
      sms: `sms:${digits}`,
    });
  });
  return out;
};

// Primary address first, then the Atlas-only aliases (contact_emails).
const contactEmails = (raw) => {
  const out = [];
  const seen = new Set();
  const add = (e, label) => {
    const v = (e || '').trim();
    if (!v || seen.has(v.toLowerCase())) return;
    seen.add(v.toLowerCase());
    out.push({ email: v, label, href: `mailto:${v}` });
  };
  if (raw) {
    add(raw.email, 'primary');
    (raw.emails || []).forEach((a) => add(a.email, a.label || (a.source === 'merge' ? 'from a merged duplicate' : 'also')));
  }
  return out;
};

// No chat bubble in the icon set — drawn here for the Text action.
const PhoneMsgIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12z" />
  </svg>
);

window.AtlasPhone = { parsePhones, contactEmails, MsgIcon: PhoneMsgIcon };

// ── The list ────────────────────────────────────────────────────────────────
const tapStyle = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };

// One chip at a time — each maps onto a server-side /api/contacts filter the
// desktop Filter popover already uses (role, referred_only, has_phone).
const PEOPLE_PHONE_CHIPS = [
  { value: 'all',              label: 'All' },
  { value: 'phone',            label: 'Has phone' },
  { value: 'referred',         label: 'Referred' },
  { value: 'seller',           label: 'Sellers' },
  { value: 'buyer',            label: 'Buyers' },
  { value: 'gpp',              label: 'GPP' },
  { value: 'referral_partner', label: 'Referral partners' },
];
const peopleChipParams = (chip) => {
  if (chip === 'phone') return { has_phone: true };
  if (chip === 'referred') return { referred_only: true };
  if (chip && chip !== 'all') return { role: chip };
  return {};
};
const PEOPLE_PHONE_PAGE = 50;

// Back from a person lands on the list as it was left: search, chip, the
// pages loaded and the scroll position (window.PhoneListMemo, defined in
// mobile/deals.jsx — the rules are there). Made on first use rather than at
// load so this file doesn't depend on the bundle order.
let ppMemoObj = null;
const ppMemo = () => ppMemoObj || (ppMemoObj = window.PhoneListMemo.create(['atlas-data-changed']));

// 44px round icon button — an <a> when there's exactly one target, a button
// that opens the picker when there are several. `linkClick` replaces the
// link's own onClick (the do-not-call check, which also stops the click).
const RowIconAction = ({ label, icon, href, onClick, linkClick }) => {
  const style = {
    ...tapStyle, width: 44, height: 44, borderRadius: 22, flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--accent-soft)', color: 'var(--accent-text)', border: 'none', cursor: 'pointer', textDecoration: 'none',
  };
  // stopPropagation: the row behind opens the person; these must only dial / compose.
  if (href) return <a href={href} aria-label={label} onClick={linkClick || ((e) => e.stopPropagation())} style={style}>{icon}</a>;
  return <button type="button" aria-label={label} onClick={(e) => { e.stopPropagation(); onClick(); }} style={style}>{icon}</button>;
};

// Someone the calling team marked do not call: Call asks first
// (window.CallGuard, views/call_button.jsx), then dials.
const personDnc = (p) => !!(p && p._raw && p._raw.do_not_call);

const PhonePersonRow = React.memo(({ p, onOpen, onPickPhone }) => {
  const raw = p._raw || {};
  const phones = parsePhones(raw.phone);
  const dnc = personDnc(p);
  const email = (raw.email || '').trim();
  const sub = [p.title !== '—' ? p.title : null, p.company !== '—' ? p.company : null].filter(Boolean).join(' · ');
  return (
    <div data-row-id={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 0 0', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
      <button type="button" onClick={() => onOpen(p.id)}
        style={{ ...tapStyle, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, minHeight: 64, padding: '10px 4px 10px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit' }}>
        <Avatar name={p.name} size={36} color="oklch(0.50 0.13 295)" />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 15.5, fontWeight: 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
          <span style={{ display: 'block', fontSize: 13, color: sub ? 'var(--text-3)' : 'var(--text-4)', lineHeight: 1.35, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sub || 'No title or company'}
          </span>
        </span>
      </button>
      {/* Fixed slots so the icons line up down the list even when a row has
          only one of the two. */}
      {phones.length
        ? <RowIconAction label={`Call ${p.name}`} icon={<Icon.Phone size={18} />}
            href={phones.length === 1 ? phones[0].tel : null}
            linkClick={dnc && phones.length === 1 && window.CallGuard ? window.CallGuard.linkClick(phones[0].tel, true, 'call', p.name) : undefined}
            onClick={() => onPickPhone(p, phones)} />
        : <span style={{ width: 44, flexShrink: 0 }} />}
      {email
        ? <RowIconAction label={`Email ${p.name}`} icon={<Icon.Mail size={18} />} href={`mailto:${email}`} />
        : <span style={{ width: 44, flexShrink: 0 }} />}
    </div>
  );
});

const PhonePeopleSkeleton = ({ rows = 8 }) => (
  <div aria-hidden="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 64, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <span className="atlas-skel" style={{ width: 36, height: 36, borderRadius: 18, flexShrink: 0 }} />
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="atlas-skel" style={{ width: `${55 + ((i * 17) % 30)}%`, height: 13, borderRadius: 4 }} />
          <span className="atlas-skel" style={{ width: `${35 + ((i * 23) % 40)}%`, height: 11, borderRadius: 4 }} />
        </span>
      </div>
    ))}
  </div>
);

const PhonePeople = ({ goToContact, activeUser }) => {
  const PLM = window.PhoneListMemo;
  const memo = ppMemo();
  // Same user and team scope as when the list was left, or start over.
  const who = PLM.who(activeUser);
  const [saved] = React.useState(() => (activeUser?.id && memo.ui && memo.who === who ? memo.ui : null));
  const [q, setQ] = React.useState(saved ? saved.q : '');
  const [debouncedQ, setDebouncedQ] = React.useState(saved ? saved.q.trim() : '');
  const [chip, setChip] = React.useState(saved ? saved.chip : 'all');

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Same scope + filters as the desktop grid's buildParams (my_team, q,
  // referred_only / role / has_phone) — server-side, so they cover every
  // contact, not just the pages loaded.
  const params = React.useMemo(
    () => ({ my_team: true, ...(debouncedQ ? { q: debouncedQ } : {}), ...peopleChipParams(chip) }),
    [debouncedQ, chip]
  );
  const paramsKey = JSON.stringify(params);
  const [resumed] = React.useState(() => (saved ? PLM.rowsFor(memo, `${who}|${paramsKey}`, PEOPLE_PHONE_PAGE) : null));

  const [items, setItems] = React.useState(resumed ? resumed.items : null);   // null = first page loading
  const [total, setTotal] = React.useState(resumed ? resumed.total : 0);
  const [error, setError] = React.useState(null);
  const [page, setPage] = React.useState(resumed ? resumed.page : 1);
  const [paging, setPaging] = React.useState(false);
  const [loadedAll, setLoadedAll] = React.useState(resumed ? resumed.loadedAll : false);
  const [phonePick, setPhonePick] = React.useState(null);   // { name, phones }
  const sentinelRef = React.useRef(null);
  const scrollRef = React.useRef(null);
  const reqSeq = React.useRef(0);
  const queryGen = React.useRef(0);   // bumped by every page-1 load; a quiet refresh from before it is dropped
  const refreshSeq = React.useRef(0);
  const loadedKey = React.useRef(resumed ? paramsKey : null);   // the query `items` answers
  const fetchedAt = React.useRef(resumed ? memo.fetchedAt : 0);
  const anchorSnap = React.useRef(null);
  const live = React.useRef(null);
  live.current = { items, page, error };

  const fetchPage = React.useCallback(async (p, append) => {
    const seq = ++reqSeq.current;
    if (!append) queryGen.current += 1;
    const startedAt = Date.now();
    setPaging(true);
    try {
      const res = await window.AtlasApi.listContacts({ ...params, page: p, limit: PEOPLE_PHONE_PAGE });
      if (seq !== reqSeq.current) return;   // a newer search superseded this one
      const rows = ((res && res.items) || []).map((r) => window.AtlasData.__adaptContact(r));
      const newTotal = (res && typeof res.total === 'number') ? res.total : rows.length;
      loadedKey.current = paramsKey;
      if (!append) fetchedAt.current = startedAt;
      setItems((prev) => {
        const next = append && prev ? [...prev, ...rows.filter((r) => !prev.some((x) => x.id === r.id))] : rows;
        if (rows.length === 0 || next.length >= newTotal) setLoadedAll(true);
        return next;
      });
      setTotal(newTotal);
      setPage(p);
      setError(null);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      if (!append) { setItems([]); setTotal(0); setLoadedAll(true); setError(e.message || 'Could not load people'); }
      else pushToast({ type: 'error', title: 'Could not load more people', body: e.message });
    } finally {
      if (seq === reqSeq.current) setPaging(false);
    }
  }, [params, paramsKey]);

  const reload = React.useCallback(() => {
    setItems(null);
    setPage(1);
    setLoadedAll(false);
    setError(null);
    return fetchPage(1, false);
  }, [fetchPage]);

  // Re-read every page loaded so far in one request and swap the rows in
  // place: no skeleton, and the rows on screen stay put. Runs on the way
  // back to a remembered list, and on change events while the list is open.
  const refreshQuietly = React.useCallback(async () => {
    const cur = live.current;
    if (cur.items == null || cur.error) return;
    const covered = Math.min(Math.max(1, cur.page) * PEOPLE_PHONE_PAGE, PLM.REFRESH_MAX);
    const gen = queryGen.current;
    const seq = ++refreshSeq.current;
    const startedAt = Date.now();
    try {
      const res = await window.AtlasApi.listContacts({ ...params, page: 1, limit: covered });
      if (gen !== queryGen.current || seq !== refreshSeq.current) return;
      const fresh = ((res && res.items) || []).map((r) => window.AtlasData.__adaptContact(r));
      const newTotal = (res && typeof res.total === 'number') ? res.total : fresh.length;
      anchorSnap.current = PLM.anchor(scrollRef.current);
      fetchedAt.current = startedAt;
      setItems((prev) => PLM.mergeHead(prev, fresh, covered));
      setTotal(newTotal);
      setLoadedAll(fresh.length < covered || fresh.length >= newTotal);
    } catch (_) { /* keep what's on screen */ }
  }, [params]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => {
    if (!anchorSnap.current) return;
    PLM.reanchor(scrollRef.current, anchorSnap.current);
    anchorSnap.current = null;
  }, [items]);

  // A remembered list comes back where it was scrolled to, refreshed
  // underneath only if it may be out of date. A new query starts at the top.
  React.useLayoutEffect(() => {
    if (resumed) PLM.restoreScroll(scrollRef.current, memo.scrollTop);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const firstLoad = React.useRef(true);
  React.useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      if (resumed) { if (PLM.stale(memo)) refreshQuietly(); return; }
    } else {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }
    memo.scrollTop = 0;   // a fresh list never inherits the last one's offset
    reload();
  }, [reload]);   // eslint-disable-line react-hooks/exhaustive-deps

  // A person created from the + sheet (or edited elsewhere) shows up here.
  // A list that failed or is still loading reloads instead (e.g. the app
  // coming back online on resume), as it always did.
  React.useEffect(() => {
    const onChanged = () => {
      const cur = live.current;
      if (cur.items == null || cur.error) reload(); else refreshQuietly();
    };
    window.addEventListener('atlas-data-changed', onChanged);
    return () => window.removeEventListener('atlas-data-changed', onChanged);
  }, [reload, refreshQuietly]);

  // Remember the list for the trip back from a person.
  React.useEffect(() => {
    Object.assign(memo, { who, ui: { q, chip } });
  }, [who, q, chip]);
  React.useEffect(() => {
    if (error) { memo.rows = null; return; }
    if (items == null || loadedKey.current == null) return;
    Object.assign(memo, { key: `${who}|${loadedKey.current}`, rows: { items, total, page, loadedAll }, fetchedAt: fetchedAt.current });
  }, [who, items, total, page, loadedAll, error]);

  const hasMore = items != null && !loadedAll && items.length < total;
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    // Root = the page scroller, so the 400px margin pre-loads the next page
    // before the end is reached (against the viewport it would be clipped).
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !paging) fetchPage(page + 1, true);
    }, { root: scrollRef.current || null, rootMargin: '400px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, paging, page, fetchPage]);

  // The person's page can edit them without announcing it, so the list
  // re-reads itself on the way back.
  const open = React.useCallback((id) => {
    if (!goToContact) return;
    if (scrollRef.current) memo.scrollTop = scrollRef.current.scrollTop;
    PLM.touch(memo);
    goToContact(id);
  }, [goToContact]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pickPhone = React.useCallback((p, phones) => setPhonePick({ name: p.name, phones, dnc: personDnc(p) }), []);

  const chipLabel = (PEOPLE_PHONE_CHIPS.find((c) => c.value === chip) || {}).label;
  const filtered = !!debouncedQ || chip !== 'all';

  return (
    <window.PhoneScroll ref={scrollRef} style={PLM.SCROLL_STYLE} onScroll={(e) => { memo.scrollTop = e.currentTarget.scrollTop; }}>
      {/* Search + chips stay pinned while the list scrolls under them. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg)', paddingTop: 10, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
        <div style={{ padding: '0 16px' }}>
          <window.PhoneSearchField value={q} onChange={setQ} placeholder="Name, company or email" />
        </div>
        <window.PhoneChips value={chip} onChange={setChip} options={PEOPLE_PHONE_CHIPS} style={{ marginTop: 10 }} />
      </div>

      <div style={{ padding: '10px 16px 6px', fontSize: 13, color: 'var(--text-3)' }}>
        {items === null
          ? 'Loading…'
          : `${total.toLocaleString()} ${total === 1 ? 'person' : 'people'}${filtered ? ' match' : ''}`}
      </div>

      {items === null && <PhonePeopleSkeleton />}

      {items && error && (
        <window.PhoneEmpty title="Couldn't load people" body={error}
          action={<Btn variant="outline" onClick={reload}>Try again</Btn>} />
      )}

      {items && !error && items.length === 0 && (
        <window.PhoneEmpty
          title={debouncedQ ? `No one matches “${debouncedQ}”` : 'No people here'}
          body={chip !== 'all'
            ? `Nobody under “${chipLabel}”${debouncedQ ? ' with that search' : ''}. Try All.`
            : 'Search covers names, companies and email addresses.'}
          action={chip !== 'all' ? <Btn variant="outline" onClick={() => setChip('all')}>Show everyone</Btn> : null} />
      )}

      {items && items.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {items.map((p) => <PhonePersonRow key={p.id} p={p} onOpen={open} onPickPhone={pickPhone} />)}
        </div>
      )}
      {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      {paging && items && items.length > 0 && <PhonePeopleSkeleton rows={2} />}
      {items && items.length > 0 && !hasMore && (
        <div style={{ padding: '14px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-4)' }}>
          {items.length.toLocaleString()} {items.length === 1 ? 'person' : 'people'} · end of list
        </div>
      )}

      <window.PhoneSheet open={!!phonePick} onClose={() => setPhonePick(null)} title={phonePick ? `Call ${phonePick.name}` : ''}>
        <window.PhoneActionList items={((phonePick && phonePick.phones) || []).map((ph) => ({
          key: ph.tel, label: ph.display, sub: ph.label, icon: <Icon.Phone size={18} />,
          // Navigating to tel: hands off to the dialler; the sheet closes behind
          // it. A do-not-call person is asked about first.
          onClick: () => {
            const pick = phonePick;
            setPhonePick(null);
            if (pick && pick.dnc && window.CallGuard) window.CallGuard.dial(ph.tel, true, 'call', pick.name);
            else window.location.href = ph.tel;
          },
        }))} />
      </window.PhoneSheet>
    </window.PhoneScroll>
  );
};

window.PhonePeople = PhonePeople;
